import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { 
  createBlogPost, 
  updateBlogPost, 
  getBlogPost,
  getAllCategories
} from '../../firebase/models/blogService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/config';

// Styled components
const Container = styled.div`
  padding: var(--spacing-xl);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-xl);
`;

const Title = styled.h1`
  margin: 0;
`;

const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 500;
  margin-bottom: var(--spacing-sm);
`;

const Input = styled.input`
  padding: var(--spacing-md);
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
`;

const Textarea = styled.textarea`
  padding: var(--spacing-md);
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  min-height: 200px;
  resize: vertical;
`;

const FileInput = styled.input`
  padding: var(--spacing-md);
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: var(--spacing-md);
  margin-top: var(--spacing-md);
`;

const Button = styled.button`
  padding: var(--spacing-md);
  background-color: ${props => props.secondary ? '#f0f0f0' : 'var(--accent-blue)'};
  color: ${props => props.secondary ? 'inherit' : 'white'};
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.secondary ? '#e0e0e0' : 'var(--dark-blue)'};
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const Message = styled.p`
  padding: var(--spacing-md);
  background-color: ${props => props.type === 'error' ? '#ffeeee' : '#eeffee'};
  border-radius: 4px;
  margin-bottom: var(--spacing-md);
`;

const ImagePreview = styled.div`
  margin-top: var(--spacing-md);
  
  img {
    max-width: 300px;
    max-height: 200px;
    border-radius: 4px;
    border: 1px solid #ddd;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  margin-top: var(--spacing-sm);
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
`;

const CategoryContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: var(--spacing-md);
  margin-top: var(--spacing-sm);
`;

const CategoryOption = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
`;

// Custom styles for the React Quill editor
const EditorContainer = styled.div`
  .ql-container {
    min-height: 300px;
    font-size: 16px;
    font-family: inherit;
  }
  
  .ql-editor {
    min-height: 300px;
  }
`;

const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

// Custom image handler for Quill to upload images to Firebase Storage
function imageHandler() {
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', 'image/*');
  input.click();

  input.onchange = async () => {
    const file = input.files[0];
    if (file) {
      // Upload to Firebase Storage
      const imageRef = ref(storage, `blog/${Date.now()}_${file.name}`);
      await uploadBytes(imageRef, file);
      const url = await getDownloadURL(imageRef);

      // Insert image into editor
      const quill = this.quill;
      const range = quill.getSelection();
      quill.insertEmbed(range.index, 'image', url);
    }
  };
}

// Quill modules to attach to editor
const quillModules = {
  toolbar: {
    container: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
      ['link', 'image'],
      ['clean']
    ],
    handlers: {
      image: imageHandler
    }
  }
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet', 'indent',
  'link', 'image'
];

const PostEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    published: true,
    categories: []
  });
  const [featuredImage, setFeaturedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(!isEditMode); // Only false if editing
  
  // Fetch post data if in edit mode
  useEffect(() => {
    const fetchData = async () => {
      if (isEditMode) {
        try {
          console.log("Fetching post data for ID:", id);
          const postData = await getBlogPost(id);
          console.log("Received post data:", postData);
          
          if (!postData) {
            throw new Error("Post data was empty");
          }
          
          setFormData({
            title: postData.title || '',
            slug: postData.slug || '',
            excerpt: postData.excerpt || '',
            content: postData.content || '',
            published: postData.published !== false, // default to true
            categories: postData.categories || []
          });
          setDataLoaded(true);
          
          console.log("Form data set:", {
            title: postData.title,
            slug: postData.slug,
            excerpt: postData.excerpt?.substring(0, 50) + '...',
          });
          
          if (postData.featuredImage) {
            setImagePreview(postData.featuredImage);
          }
        } catch (err) {
          console.error('Error fetching post:', err);
          setError(`Failed to load post data: ${err.message}`);
        }
      } else {
        setDataLoaded(true);
      }
    };
    
    const fetchCategories = async () => {
      try {
        const categoriesData = await getAllCategories();
        setCategories(categoriesData);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    
    fetchCategories();
    fetchData();
  }, [id, isEditMode]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-generate slug when title changes
    if (name === 'title' && (!formData.slug || formData.slug === generateSlug(formData.title))) {
      setFormData({
        ...formData,
        title: value,
        slug: generateSlug(value)
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleContentChange = (content) => {
    setFormData({
      ...formData,
      content
    });
  };
  
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };
  
  const handleCategoryChange = (categoryId, checked) => {
    if (checked) {
      setFormData({
        ...formData,
        categories: [...formData.categories, categoryId]
      });
    } else {
      setFormData({
        ...formData,
        categories: formData.categories.filter(id => id !== categoryId)
      });
    }
  };
  
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFeaturedImage(file);
      
      // Create a preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      
      // Clean up the URL when component unmounts
      return () => URL.revokeObjectURL(previewUrl);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      let result;
      
      if (isEditMode) {
        result = await updateBlogPost(id, formData, featuredImage);
        setSuccess('Post updated successfully!');
      } else {
        result = await createBlogPost(formData, featuredImage);
        setSuccess('Post created successfully!');
        
        // Reset form after successful creation
        setFormData({
          title: '',
          slug: '',
          excerpt: '',
          content: '',
          published: true,
          categories: []
        });
        setFeaturedImage(null);
        setImagePreview('');
      }
      
      // Navigate back to post list after a short delay
      setTimeout(() => {
        navigate('/admin/posts');
      }, 2000);
      
    } catch (err) {
      console.error('Error saving post:', err);
      setError('Failed to save post. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container>
      <Header>
        <Title>{isEditMode ? 'Edit Post' : 'Create New Post'}</Title>
      </Header>
      
      {error && <Message type="error">{error}</Message>}
      {success && <Message>{success}</Message>}
      
      {!dataLoaded ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>Loading post data...</div>
      ) : (
      <Card>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="slug">Slug *</Label>
            <Input
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="excerpt">Excerpt</Label>
            <Textarea
              id="excerpt"
              name="excerpt"
              value={formData.excerpt}
              onChange={handleInputChange}
              rows={3}
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="content">Content *</Label>
            <EditorContainer>
              <ReactQuill
                theme="snow"
                value={formData.content}
                onChange={handleContentChange}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Write your blog post content here..."
              />
            </EditorContainer>
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="featuredImage">Featured Image</Label>
            <FileInput
              id="featuredImage"
              name="featuredImage"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
            />
            {imagePreview && (
              <ImagePreview>
                <img src={imagePreview} alt="Preview" />
              </ImagePreview>
            )}
          </FormGroup>
          
          <FormGroup>
            <Label>Categories</Label>
            <CategoryContainer>
              {categories.map(category => (
                <CategoryOption key={category.id}>
                  <Checkbox
                    type="checkbox"
                    id={`category-${category.id}`}
                    checked={formData.categories.includes(category.id)}
                    onChange={(e) => handleCategoryChange(category.id, e.target.checked)}
                  />
                  <label htmlFor={`category-${category.id}`}>{category.name}</label>
                </CategoryOption>
              ))}
            </CategoryContainer>
          </FormGroup>
          
          <FormGroup>
            <CheckboxContainer>
              <Checkbox
                id="published"
                name="published"
                type="checkbox"
                checked={formData.published}
                onChange={handleCheckboxChange}
              />
              <Label htmlFor="published">Publish post</Label>
            </CheckboxContainer>
          </FormGroup>
          
          <ButtonContainer>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : isEditMode ? 'Update Post' : 'Create Post'}
            </Button>
            <Button 
              type="button" 
              secondary 
              onClick={() => navigate('/admin/posts')}
            >
              Cancel
            </Button>
          </ButtonContainer>
        </Form>
      </Card>
      )}
    </Container>
  );
};

export default PostEditor; 